<template>
  <div>
    <form>
      <div class="form-group">
        <label class="form-control-label">Account manager:</label>
        <div class="row">
          <div class="col-sm-12 col-xl-6">
            <el-autocomplete
              @select="(e) => handleSyncDetails(e)"
              @blur="handleAutoCompleteBlur"
              v-model="accountDetails.accountManager.name"
              :fetch-suggestions="querySearchAsync"
              value-key="name"
              :trigger-on-focus="false"
              clearable
              class="w-100"
            />
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-control-label">Contract term months:</label>
        <div class="row">
          <div class="col-sm-12 col-xl-6">
            <validation-provider name="contractTermMonths" rules="min_value:0" v-slot="{ validated, errors }">
              <input
                type="number"
                id="contractTermMonths"
                class="form-control"
                :class="{
                  'is-invalid': validated && !!errors.length
                }"
                v-model.number="accountDetails.contractTermMonths"
              />
              <span id="contractTermMonths" :class="{ 'invalid-feedback': validated && !!errors.length }">{{
                errors[0]
              }}</span>
            </validation-provider>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-control-label">Contract dates:</label>
        <div class="row">
          <div class="col-sm-12 col-md-6 col-lg-6 col-xl-3">
            <el-date-picker
              v-model="accountDetails.contractStart"
              @change="handleContractStartChange"
              class="d-block w-100"
              value-format="timestamp"
              format="dd/MM/yyyy"
              type="date"
              placeholder="Set start date"
            />
          </div>
          <div class="col-sm-12 col-md-6 col-lg-6 col-xl-3">
            <el-date-picker
              v-model="accountDetails.contractEnd"
              class="d-block w-100"
              value-format="timestamp"
              format="dd/MM/yyyy"
              type="date"
              placeholder="Set renewal date"
            />
          </div>
        </div>
      </div>

      <div class="form-group">
        <div class="row">
          <div class="col-sm-12 col-md-6 col-lg-6 col-xl-3">
            <label class="form-control-label">Katana order number:</label>
            <simple-editable-list
              :defaultValue="accountDetails.katanaOrderNumber"
              @onListChange="(ev) => handleListChange(ev, DETAIL_KEY.KATANA_ORDER_NUMBER)"
              allow-delete
            />
          </div>

          <div class="col-sm-12 col-md-6 col-lg-6 col-xl-3">
            <label class="form-control-label">Shipment Number:</label>
            <simple-editable-list
              :defaultValue="accountDetails.shipmentNumber"
              @onListChange="(ev) => handleListChange(ev, DETAIL_KEY.SHIPMENT_NUMBER)"
              allow-delete
            />
          </div>
        </div>
      </div>

      <div class="form-group">
        <div class="row">
          <div class="col-sm-12 col-md-6 col-lg-6 col-xl-3">
            <label class="form-control-label">Invoice number:</label>
            <simple-editable-list
              :defaultValue="accountDetails.invoiceNumber"
              @onListChange="(ev) => handleListChange(ev, DETAIL_KEY.INVOICE_NUMBER)"
              allow-delete
            />
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-control-label">Diagnostic script name:</label>
        <div class="row">
          <div class="col-sm-12 col-xl-6">
            <input type="text" class="form-control" v-model="accountDetails.diagnosticScriptName" />
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-control-label">Region:</label>
        <div class="row">
          <div class="col-sm-12 col-xl-6">
            <input type="text" class="form-control" v-model="accountDetails.region" />
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-control-label">App subscriptions:</label>
        <div class="row">
          <div class="col-sm-12 col-xl-6">
            <validation-provider name="appSubscription" rules="min_value:0" v-slot="{ validated, errors }">
              <input
                type="number"
                id="appSubscription"
                class="form-control"
                :class="{
                  'is-invalid': validated && !!errors.length
                }"
                v-model.number="accountDetails.appSubscriptions"
              />

              <span id="appSubscription" :class="{ 'invalid-feedback': validated && !!errors.length }">{{
                errors[0]
              }}</span>
            </validation-provider>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label class="form-control-label">Comment:</label>
        <div class="row">
          <div class="col-sm-12 col-xl-6">
            <textarea rows="5" type="number" class="form-control" v-model="accountDetails.comments" />
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { Select, Option, DatePicker, Autocomplete } from 'element-ui';
import moment from 'moment';
import SimpleEditableList from '../Simple/SimpleEditableList.vue';
import { TimeHelper } from '@/helpers/TimeHelper';
import api from '@/api';

const DETAIL_KEY = {
  KATANA_ORDER_NUMBER: 'katanaOrderNumber',
  SHIPMENT_NUMBER: 'shipmentNumber',
  INVOICE_NUMBER: 'invoiceNumber'
};

export default {
  props: {
    accountDetails: {
      type: Object,
      required: true
    }
  },
  components: {
    SimpleEditableList,
    [Autocomplete.name]: Autocomplete,
    [Select.name]: Select,
    [DatePicker.name]: DatePicker,
    [Option.name]: Option
  },
  data() {
    return {
      DETAIL_KEY
    };
  },
  methods: {
    handleListChange({ updatedItems }, key) {
      this.accountDetails[key] = [...updatedItems];
    },

    async querySearchAsync(queryString, cb) {
      const endpoint = 'getUsers';

      const params = {
        limit: 6,
        direction: 'desc',
        filter: `name: ${queryString}`,
        sort: 'createdOn'
      };

      const results = await api[endpoint](params);

      if (!!results.users.length) {
        cb(results.users);
      } else {
        cb([{ id: 0, name: 'No users found' }]);
      }
    },

    handleSyncDetails({ id, name }) {
      if (!!id) {
        this.accountDetails.accountManager = { id, name };

        return false;
      }

      this.accountDetails.accountManager = {};
    },

    handleAutoCompleteBlur() {
      const { id: idExist } = this.accountDetails.accountManager;
      if (!idExist) {
        this.accountDetails.accountManager = {};
      }
    },

    handleContractStartChange(val) {
      if (!this.accountDetails.contractTermMonths) return null;

      const start = moment.unix(TimeHelper.toSeconds(val));

      const end = start.clone().add(this.accountDetails.contractTermMonths, 'months');

      this.accountDetails.contractEnd = end.toDate();
    }
  }
};
</script>